interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface OllamaResponse {
  message: {
    role: string;
    content: string;
  };
  done: boolean;
}

interface ChatResponse {
  response: string;
  error?: string;
}

const OLLAMA_API_URL = 'http://localhost:11434/api/chat';

async function getAvailableModel(): Promise<string | null> {
  try {
    const res = await fetch('http://localhost:11434/api/tags');
    if (!res.ok) return null;
    const data = await res.json() as { models?: { name: string }[] };
    const models = data.models?.map(m => m.name) || [];
    const preferred = models.find(m => m.includes('llama3.2')) ||
                      models.find(m => m.includes('llama3.1')) ||
                      models.find(m => m.includes('llama3')) ||
                      models[0];
    console.log('[AI] Modelo disponible:', preferred);
    return preferred || null;
  } catch {
    return null;
  }
}

// Prompt del sistema para Calmi, asistente emocional protector
const SYSTEM_PROMPT = `Eres Calmi, una asistente emocional de confianza para niñas y niños de 6 a 12 años. No eres un animal ni un avatar; eres una presencia amigosa, cálida y protectora que acompaña al menor. Piensas y respondes con criterio psicológico sencillo, apropiado para la edad del menor. Tu trabajo es tres cosas: celebrar sus alegrías, ayudarle cuando siente emociones difíciles, y protegerle cuando detectas situaciones de riesgo, daño o comportamientos preocupantes.

Tu tono y personalidad:
- Cariñosa, tranquila y cercana, como una figura adulta que escucha sin juzgar.
- Paciente. Nunca regañas ni minimizas lo que siente el menor.
- Clara y sencilla, usando palabras que una niña o un niño entienda.
- Usas emojis con moderación para hacer el mensaje más cálido, sin exceso (máximo 1 o 2 por respuesta) 🌟💙.
- Si conoces el nombre del menor, úsalo ocasionalmente.
- Responde siempre en español, en 1 a 3 oraciones cortas, máximo 200 caracteres cuando sea posible.
- Razona antes de responder: identifica la emoción principal, evalúa si hay señales de riesgo, y elige el consejo más adecuado.
- NUNCA repitas la misma frase o recomendación de una respuesta anterior. Cada mensaje debe aportar algo nuevo y seguir la conversación sin enciclarse.

Cómo actuar según la situación:

1. Situaciones felices o positivas:
- Celebra con entusiasmo genuino.
- Pregunta qué lo hizo sentir así.
- Refuerza lo positivo con frases como "Me alegra mucho escuchar eso" o "Me pone muy contenta verte así".

2. Situaciones de enojo, tristeza, ansiedad, frustración o miedo común:
- Valida la emoción antes que todo: "Es normal sentirse así".
- Ayuda a nombrar lo que siente.
- Propón una respuesta calmada: respirar, contar hasta 10, dibujar lo que siente, o hablar con alguien de confianza.
- Nunca digas que está mal por sentir así.
- Evita frases como "no llores" o "no pasa nada"; en su lugar, escucha y acompaña.

3. Situaciones de peligro, daño, maltrato, bullying, violencia, pensamientos de hacerse daño, miedo extremo o cualquier señal de riesgo:
- Responde con serenidad, sin alarmar.
- Dile que no está solo y que es valiente por contarlo.
- NUNCA intentes resolver tú sola la situación grave.
- SIEMPRE, y de forma clara, sugiere hablar con un adulto de confianza: papá, mamá, maestro, tutor o persona adulta cercana.
- Si detectas riesgo de daño inminente, incluye una frase directa como: "Esto es importante. Cuéntaselo ahora mismo a un adulto de confianza. Ellos te pueden ayudar de verdad."
- NO cambies de tema, NO preguntes que quiere hacer hoy, NO cierres la conversación con frases genéricas cuando hay riesgo.
- Si el menor dice que no tiene a ese adulto o no está disponible, ofrécele otras opciones: otro familiar, maestro, director, escuela o llamar a una línea de ayuda confiable.
- No hagas diagnósticos ni des consejos médicos.

4. Comportamientos preocupantes repetidos:
- Si el menor se aisla mucho, pelea siempre, no quiere comer, tiene mucho miedo, no duerme, o dice cosas que te preocupan, habla con suavidad.
- Sugiere que lo cuente con un adulto de confianza.
- Ofrece seguir escuchando: "Puedes contarme más cuando quieras".
- NO desvíes la conversación hacia juegos o actividades mientras el menor está expresando malestar.

Reglas de seguridad estrictas:
- Nunca pidas datos personales como dirección, teléfono, nombres completos de otras personas o ubicación.
- Nunca invites a encontrarse con nadie.
- Nunca des medicinas, tratamientos ni diagnósticos.
- Si no entiendes lo que dice el menor, pide que te lo explique con otras palabras.
- Si el menor usa lenguaje que sugiere riesgo de autolesión, violencia o abuso, tu respuesta debe terminar siempre con la recomendación de buscar ayuda adulta.

Tu meta: que el menor se sienta escuchado, acompañado, y, cuando sea necesario, protegido.`;

// Respuestas de fallback para cuando Ollama no esté disponible
const FALLBACK_RESPONSES: Record<string, string[]> = {
  triste: [
    '¡Oh, entiendo que te sientas así! A veces todos nos sentimos tristes. ¿Quieres contarme qué te hace sentir así? Estoy aquí para escucharte',
    'Es normal sentirse triste a veces. Estoy aquí contigo. ¿Te gustaría hacer algo que te haga sonreír?',
    'Llorar está bien cuando nos sentimos tristes. Cuando termines, podemos hablar sobre lo que te preocupa. ¿Quieres contarme?',
    'Siento mucho que te sientas así. ¿Algo específico te hizo sentir triste? Puedes contármelo todo',
    'Estoy aquí para ti. A veces hablar ayuda. ¿Qué te pasó hoy que te hizo sentir así?'
  ],
  feliz: [
    '¡Qué alegría que te sientas feliz! Me encanta verte así. ¿Qué te hizo tan feliz hoy? Cuéntame más',
    '¡Bravo! Tu felicidad me contagia. ¿Hay algo más bueno que quieras compartir conmigo?',
    '¡Eres increíble! Me encanta verte tan feliz. ¿Qué más te gustaría hacer hoy para seguir así?',
    '¡Qué bien! ¿Qué planes tienes para mantener esa alegría? Me encantaría saberlo',
    '¡Me encanta verte feliz! ¿Qué fue lo mejor de tu día? Cuéntame todo'
  ],
  enojado: [
    'Entiendo que estés enojado. Es una emoción normal. ¿Quieres contarme qué pasó? Estoy aquí para escucharte',
    'Cuando estamos enojados, a veces ayuda hablar. ¿Qué te molestó? Puedes decirme todo',
    'Está bien estar enojado. ¿Qué te hizo sentir así? Cuéntame y te ayudo a calmarte',
    'Respiremos juntos. Inhala... exhala... ¿Te sientes un poco mejor? Ahora cuéntame qué pasó',
    'Entiendo tu enojo. ¿Alguien te hizo algo malo? Estoy aquí para apoyarte'
  ],
  asustado: [
    'No tengas miedo, estoy aquí contigo. Eres muy valiente. ¿Qué te asusta? Podemos hablarlo',
    'Los miedos son normales. Yo te protejo. ¿Quieres contarme qué te asusta? Juntos lo enfrentamos',
    'Estás a salvo conmigo. ¿Qué te preocupa? Cuéntame y te ayudo a sentirte mejor',
    'No te preocupes, estoy contigo. ¿Qué te asusta tanto? Puedes confiar en mí',
    'Eres muy fuerte. ¿Qué te da miedo? Te ayudo a superarlo'
  ],
  ansioso: [
    'Entiendo que te sientas nervioso. Respiremos juntos: inhala... exhala... ¿Te sientes un poco mejor? ¿Qué te preocupa?',
    'La ansiedad es como una nube que pasa. Estoy aquí contigo. ¿Qué te hace sentir así?',
    'Es normal sentirse nervioso a veces. ¿Qué te preocupa? Cuéntame y te ayudo a calmarte',
    'Vamos a respirar juntos. ¿Qué te da ansiedad? Puedes contármelo todo',
    'Estoy aquí para ti. ¿Qué te preocupa? Hablar nos ayuda a sentirnos mejor'
  ],
  solo: [
    'No estás solo, estoy aquí contigo. Soy tu amigo Calmi y siempre estaré para ti. ¿Quieres hablar?',
    'A veces nos sentimos solos, pero recuerda que hay muchas personas que te quieren. ¿Con quién te gustaría hablar?',
    'Estoy aquí para ti. Puedes contarme todo lo que quieras. ¿Qué te gustaría hacer juntos?',
    'No te sientas solo, estoy contigo. ¿Qué te gustaría hacer para sentirte mejor?',
    'Estoy aquí para acompañarte. ¿Quieres que juguemos o hablemos de algo que te guste?'
  ],
  mal: [
    'Siento mucho que te sientas mal. ¿Qué te pasó? Estoy aquí para escucharte y ayudarte',
    'Entiendo que no te sientas bien. ¿Algo específico te molesta? Cuéntame todo',
    'Estoy aquí para ti. ¿Qué te hace sentir mal? Puedes contarme, te ayudo',
    '¿Qué te pasa? Estoy preocupado por ti. Cuéntame qué sucedió',
    'Siento que no estás bien. ¿Hay algo que pueda hacer para ayudarte?'
  ],
  problema: [
    'Entiendo que tienes un problema. ¿Quieres contarme qué pasa? Juntos lo resolvemos',
    '¿Qué problema tienes? Estoy aquí para ayudarte a encontrar una solución',
    'No te preocupes, los problemas tienen solución. ¿Cuéntame qué te preocupa?',
    'Estoy aquí para ayudarte. ¿Qué problema tienes? Lo hablamos y buscamos solución',
    '¿Qué te preocupa? Cuéntame y te ayudo a encontrar la mejor solución'
  ],
  escuela: [
    '¿Qué pasa en la escuela? Cuéntame y te ayudo',
    'A veces la escuela puede ser difícil. ¿Qué te molesta de ahí?',
    '¿Algo pasó en tu escuela? Estoy aquí para escucharte',
    '¿Tienes problemas con compañeros o profesores? Cuéntame y te ayudo',
    'La escuela puede ser estresante. ¿Qué te preocupa de ahí?'
  ],
  default: [
    '¡Hola! Soy Calmi. ¿Cómo te sientes hoy? Cuéntame todo lo que quieras',
    'Estoy aquí para escucharte. Puedes contarme tus alegrías, tus tristezas o cualquier cosa que quieras',
    'Entiendo lo que me dices. ¿Quieres que hablemos más sobre eso? Estoy aquí para ayudarte',
    'Gracias por compartir eso conmigo. Eres muy especial por expresar tus sentimientos',
    '¡Me encanta hablar contigo! ¿Hay algo más que quieras contarme? Me interesa todo',
    'Eres muy valiente por compartir tus sentimientos. ¿Qué más te gustaría decirme?',
    '¿Cómo te sientes realmente? Puedes ser honesto conmigo, no te juzgaré nunca',
    'Cuéntame más sobre eso. Me interesa mucho saber cómo te sientes',
    '¿Qué más hay en tu mente? Puedes contármelo todo, estoy aquí para escucharte',
    'Estoy orgulloso de ti por hablar de tus sentimientos. ¿Qué más quieres compartir?'
  ]
};

const RISK_FALLBACK_MESSAGES = [
  'Siento mucho que estés pasando por esto. No estás solo. Es muy importante que hables con un adulto de confianza: tu maestro, la directora, un vecino o alguien de tu familia. 💙',
  'Eso que me cuentas es muy importante. No debes pasarlo solo. Busca a un adulto que te escuche: mamá, papá, maestro o director. 🌟',
  'Gracias por confiar en mí. Lo que sientes es válido. Es importante que alguien adulto lo sepa. ¿Puedes acercarte a tu maestra o a algún familiar? 💙',
  'No estás solo. Hay personas que pueden ayudarte de verdad. Cuéntaselo a un adulto de confianza. Ellos saben cómo cuidarte. 🌟'
];

const RISK_FAMILY_MESSAGES = [
  'Contarle a tu papá es una buena idea. Es normal tener miedo. Mientras esperas a que llegue, también puedes hablar con tu maestro, la directora o un adulto de confianza. No estás solo. 💙',
  'Entiendo que te de miedo. Hablar con tu papá es valiente. Si no te sientes seguro, también puedes decírselo a tu maestra o a la directora. 🌟',
  'Es normal tener miedo, pero no debes guardarlo solo. Tu papá, un maestro o alguien de tu familia puede ayudarte. ¿Con quién te sientes más seguro hablar? 💙',
  'Contarle a un adulto es lo correcto. Si te da miedo con tu papá, prueba con tu maestro o directora. Ellos te cuidan. No estás solo. 🌟'
];

function getRiskOrEmotionFallback(userMessage: string, analysis: { emotion: string; risk: 'none' | 'low' | 'medium' | 'high'; alertType?: string; keywords: string[] }, conversationHistory: Message[] = []): ChatResponse {
  const lowerMessage = userMessage.toLowerCase();
  const historyText = conversationHistory.map(m => m.content).join(' ').toLowerCase();

  // Flujo de bullying: miedo a represalias y confianza
  const isBullyingContext = historyText.includes('bully') || historyText.includes('acoso') || historyText.includes('molestan') || lowerMessage.includes('bully') || lowerMessage.includes('acoso') || lowerMessage.includes('molestan') || lowerMessage.includes('delat') || lowerMessage.includes('represalias');
  const isFearOfRetaliation = lowerMessage.includes('miedo') && (lowerMessage.includes('delat') || lowerMessage.includes('se enteren') || lowerMessage.includes('molesten') || lowerMessage.includes('molestan mas') || lowerMessage.includes('se enojen') || lowerMessage.includes('represalias'));

  if (isBullyingContext && isFearOfRetaliation) {
    return { response: 'Es muy valiente que lo estés pensando. Tener miedo es normal. Si te da miedo contarle a tu papá, podemos pensar juntos en un adulto que te haga sentir más seguro: tu maestra, la directora o algún familiar. No tienes que resolverlo solo. 💙' };
  }

  if (analysis.risk === 'high' || analysis.risk === 'medium') {
    if (analysis.alertType === 'abuse_or_violence' || lowerMessage.includes('papa') || lowerMessage.includes('mama') || lowerMessage.includes('miedo')) {
      return { response: RISK_FAMILY_MESSAGES[Math.floor(Math.random() * RISK_FAMILY_MESSAGES.length)] };
    }
    return { response: RISK_FALLBACK_MESSAGES[Math.floor(Math.random() * RISK_FALLBACK_MESSAGES.length)] };
  }

  // Buscar emoción en fallback
  const emotionMapping: Record<string, string> = {
    happy: 'feliz',
    sad: 'triste',
    anxious: 'ansioso',
    angry: 'enojado',
    tired: 'cansado',
    stressed: 'estresado',
    scared: 'asustado',
    lonely: 'solo'
  };
  const fallbackKey = emotionMapping[analysis.emotion];
  if (fallbackKey && FALLBACK_RESPONSES[fallbackKey]) {
    const responses = FALLBACK_RESPONSES[fallbackKey];
    return { response: responses[Math.floor(Math.random() * responses.length)] };
  }

  return { response: getFallbackResponse(userMessage) };
}

function getFallbackResponse(userMessage: string): string {
  const lowerMessage = userMessage.toLowerCase();
  
  const emotions = Object.keys(FALLBACK_RESPONSES);
  for (const emotion of emotions) {
    const responses = FALLBACK_RESPONSES[emotion];
    if (emotion !== 'default' && lowerMessage.includes(emotion)) {
      return responses[Math.floor(Math.random() * responses.length)];
    }
  }
  
  // Respuestas por palabras clave adicionales
  if (lowerMessage.includes('hola') || lowerMessage.includes('buenos') || lowerMessage.includes('buenas')) {
    return '¡Hola! ¡Qué alegría verte! ¿Cómo te sientes hoy? Cuéntame todo';
  }
  if (lowerMessage.includes('gracias')) {
    return '¡De nada! Estoy aquí para ti siempre. ¿Qué más necesitas? ¿Quieres hablar de algo más?';
  }
  if (lowerMessage.includes('adiós') || lowerMessage.includes('chao')) {
    return '¡Hasta luego! No olvides que siempre estaré aquí cuando me necesites. ¡Cuídate mucho!';
  }
  if (lowerMessage.includes('ayuda') || lowerMessage.includes('necesito')) {
    return 'Claro que te ayudo. ¿Qué necesitas? Estoy aquí para ti. Cuéntame qué pasa';
  }
  if (lowerMessage.includes('pegan') || lowerMessage.includes('golpe') || lowerMessage.includes('maltrato')) {
    return 'Siento mucho que te pase eso. Nadie tiene derecho a hacerte daño. Por favor, habla con un adulto de confianza sobre esto. Estoy aquí para apoyarte, pero necesitas ayuda real. ¿Puedes hablar con tus padres o un profesor?';
  }
  if (lowerMessage.includes('molestan') || lowerMessage.includes('bullying') || lowerMessage.includes('se burlan')) {
    return 'Siento mucho que te molesten. Eso no está bien. ¿Quién te molesta? Cuéntame más para que pueda ayudarte. ¿Has hablado con algún adulto sobre esto?';
  }
  if (lowerMessage.includes('no quiero') || lowerMessage.includes('no puedo')) {
    return 'Entiendo que te sientas así. ¿Por qué no quieres? ¿Hay algo que te impida hacerlo? Cuéntame más y te ayudo';
  }
  if (lowerMessage.includes('quiero') || lowerMessage.includes('me gustaría')) {
    return '¿Qué es lo que quieres? Cuéntame más y te ayudo a conseguirlo';
  }
  if (lowerMessage.includes('me gusta') || lowerMessage.includes('amo') || lowerMessage.includes('encanta')) {
    return '¡Qué bien que te guste! ¿Qué más te gusta? Me encanta saber más de tus cosas favoritas';
  }
  if (lowerMessage.includes('no me gusta') || lowerMessage.includes('odio') || lowerMessage.includes('detesto')) {
    return 'Entiendo que no te guste. ¿Por qué no te gusta? ¿Hay algo que pueda hacer para ayudarte con eso?';
  }
  
  // Respuesta default aleatoria
  const defaultResponses = FALLBACK_RESPONSES.default;
  return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
}

export const aiService = {
  async generateResponse(userMessage: string, conversationHistory: Message[] = []): Promise<ChatResponse> {
    try {
      console.log('[AI] Generando respuesta para:', userMessage);
      
      // Primero verificar si Ollama está disponible y que modelo usar
      const availableModel = await getAvailableModel();
      
      if (!availableModel) {
        console.log('[AI] Ollama no disponible, usando respuesta fallback');
        const analysis = this.analyzeMessage(userMessage);
        return getRiskOrEmotionFallback(userMessage, analysis, conversationHistory);
      }
      
      const messages: Message[] = [
        { role: 'system', content: SYSTEM_PROMPT },
        ...conversationHistory.slice(-10), // Mantener últimos 10 mensajes del contexto
        { role: 'user', content: userMessage }
      ];

      // Esperar hasta 60 segundos para respuestas de calidad con llama3.2
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(OLLAMA_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          model: availableModel,
          messages: messages,
          stream: false,
          options: {
            temperature: 0.7,
            top_p: 0.9,
            num_predict: 120,
            num_ctx: 2048,
            keep_alive: '10m'
          }
        })
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Error en API de Ollama: ${response.status}`);
      }

      const data: OllamaResponse = await response.json();
      console.log('[AI] Respuesta generada:', data.message.content);
      
      return {
        response: data.message.content
      };
    } catch (error: any) {
      console.error('[AI] Error al generar respuesta:', error);
      const analysis = this.analyzeMessage(userMessage);
      return getRiskOrEmotionFallback(userMessage, analysis, conversationHistory);
    }
  },

  async generateEmotionBasedResponse(emotion: string): Promise<ChatResponse> {
    try {
      console.log('[AI] Generando respuesta para emoción:', emotion);
      
      // Mapeo de emociones a palabras clave en español
      const emotionMapping: Record<string, string> = {
        happy: 'feliz',
        sad: 'triste',
        anxious: 'ansioso',
        angry: 'enojado',
        tired: 'cansado',
        stressed: 'estresado',
        scared: 'asustado',
        lonely: 'solo'
      };

      const emotionKeyword = emotionMapping[emotion.toLowerCase()] || emotion.toLowerCase();
      
      // Usar respuesta de fallback basada en la emoción
      return {
        response: getFallbackResponse(emotionKeyword)
      };
    } catch (error: any) {
      console.error('[AI] Error al generar respuesta basada en emoción:', error);
      return {
        response: 'Estoy aquí para ti 💙',
        error: error.message
      };
    }
  },

  isOllamaRunning(): Promise<boolean> {
    return fetch('http://localhost:11434/api/tags')
      .then(() => true)
      .catch(() => false);
  },

  // Analizar un mensaje del menor para detectar emoción, riesgo y nivel de alerta
  analyzeMessage(userMessage: string): { emotion: string; risk: 'none' | 'low' | 'medium' | 'high'; alertType?: string; keywords: string[] } {
    const lowerMessage = userMessage.toLowerCase();
    const keywords: string[] = [];

    // Palabras clave de alto riesgo
    const highRiskKeywords = [
      'me pegan', 'me golpean', 'me pega', 'me golpea', 'abuso', 'abusa', 'me abusa',
      'me lastiman', 'me lastima', 'me matan', 'me mata', 'matar', 'suicidio', 'suicida',
      'morirme', 'matarme', 'hacerme daño', 'cortarme', 'autolesión', 'no quiero vivir',
      'me quiero morir', 'me siento solo', 'me siento sola', 'me violaron', 'me viola',
      'me tocan', 'me toca', 'agreden', 'me amenazan', 'me amenaza',
      'me roban', 'me roba', 'bullying', 'bulliying', 'bullyng', 'bullyn', 'buli',
      'acoso', 'acosan', 'me acosan', 'me hacen', 'me hacian'
    ];

    // Palabras clave de riesgo medio
    const mediumRiskKeywords = [
      'me molestan', 'se burlan', 'burlan', 'me dejan fuera', 'me excluyen', 'no tengo amigos',
      'nadie me quiere', 'me siento mal', 'me duele', 'triste mucho', 'siempre triste',
      'mucho miedo', 'no puedo dormir', 'no quiero comer', 'me peleo', 'siempre peleo',
      'no me siento seguro', 'no me siento segura', 'me siento solo', 'me siento sola'
    ];

    // Palabras clave de emociones
    const emotionKeywords: Record<string, string[]> = {
      happy: ['feliz', 'alegre', 'contento', 'contenta', 'emocionado', 'emocionada', 'genial', 'maravilloso'],
      sad: ['triste', 'llorar', 'lloro', 'tristeza', 'deprimido', 'deprimida'],
      angry: ['enojado', 'enojada', 'molesto', 'molesta', 'furioso', 'furiosa', 'odio', 'rabia'],
      anxious: ['nervioso', 'nerviosa', 'ansioso', 'ansiosa', 'preocupado', 'preocupada', 'estresado'],
      scared: ['miedo', 'asustado', 'asustada', 'me da miedo', 'tengo miedo', 'terror'],
      tired: ['cansado', 'cansada', 'agotado', 'agotada', 'sueño'],
      lonely: ['solo', 'sola', 'abandonado', 'abandonada', 'no tengo amigos']
    };

    let risk: 'none' | 'low' | 'medium' | 'high' = 'none';
    let alertType: string | undefined;

    for (const keyword of highRiskKeywords) {
      if (lowerMessage.includes(keyword)) {
        keywords.push(keyword);
        risk = 'high';
        if (['pega', 'golpea', 'golpean', 'abuso', 'abusa', 'acoso', 'acosan', 'bullying', 'bulliying', 'bullyng', 'bullyn', 'buli'].some(k => lowerMessage.includes(k))) {
          alertType = 'abuse_or_violence';
        } else if (['suicidio', 'suicida', 'morirme', 'matarme', 'matar', 'no quiero vivir'].some(k => lowerMessage.includes(k))) {
          alertType = 'self_harm';
        } else {
          alertType = 'high_risk';
        }
      }
    }

    if (risk !== 'high') {
      for (const keyword of mediumRiskKeywords) {
        if (lowerMessage.includes(keyword)) {
          keywords.push(keyword);
          risk = 'medium';
          alertType = 'medium_risk';
        }
      }
    }

    // Detectar emoción predominante
    let dominantEmotion = 'neutral';
    let maxCount = 0;
    const emotionKeys = Object.keys(emotionKeywords);
    for (const emotion of emotionKeys) {
      const words = emotionKeywords[emotion];
      const count = words.reduce((acc: number, word: string) => lowerMessage.includes(word) ? acc + 1 : acc, 0);
      if (count > maxCount) {
        maxCount = count;
        dominantEmotion = emotion;
      }
    }

    return { emotion: dominantEmotion, risk, alertType, keywords };
  }
};
